import { ToastAlertTypes } from '@genial/components';
import { Utils } from '@genial/utils';
import * as zod from 'zod';

export const defaultErrorMessage: [ToastAlertTypes.Type, string, string] = [
  'error',
  'Erro ao enviar operação',
  'Não foi possível realizar está operação, tente novamente mais tarde.',
];

export const defaultSuccessMessage: [ToastAlertTypes.Type, string, string] = [
  'success',
  'Tudo certo!',
  'Os beneficiários foram alterados com sucesso.',
];

export interface BeneficiaryFormData {
  name: string;
  birthDate: Date | null;
  relationship: string;
  participation: string;
}

export interface BeneficiariesFormData {
  beneficiaries: BeneficiaryFormData[];
}

export const DEFAULT_BENEFICIARY: BeneficiaryFormData = {
  name: '',
  birthDate: null,
  relationship: '',
  participation: '0%',
};

export const VALIDATION_MESSAGES = {
  required: {
    name: 'Nome é obrigatório',
    birthDate: 'Data de nascimento é obrigatória',
    relationship: 'Parentesco é obrigatório',
    participation: 'Participação é obrigatória',
  },
  participation: {
    total: 'A participação total deve ser de 100%',
    maxValue: 'A participação não pode ser maior que 100%',
  },
} as const;

const beneficiarySchema = zod.object({
  name: zod.string().min(1, VALIDATION_MESSAGES.required.name),
  birthDate: zod.date(),
  relationship: zod.string().min(1, VALIDATION_MESSAGES.required.relationship),
  participation: zod
    .string()
    .min(1, VALIDATION_MESSAGES.required.participation)
    .transform((value) => (value.trim() === '' ? '' : value))
    .refine(
      (value) => {
        if (value.trim() === '') {
          return true;
        }
        const participation = Utils.Unmask.Percentage(value);
        return participation <= 100 && participation >= 0;
      },
      {
        message: VALIDATION_MESSAGES.participation.maxValue,
      }
    ),
});

export const beneficiariesSchema = zod.object({
  beneficiaries: zod.array(beneficiarySchema).superRefine((beneficiaries, ctx) => {
    if (!beneficiaries?.length) {
      ctx.addIssue({
        code: zod.ZodIssueCode.custom,
        message: 'É necessário pelo menos um beneficiário',
        path: [],
      });
      return;
    }

    const totalParticipation = beneficiaries.reduce(
      (total, beneficiary) => total + Utils.Unmask.Percentage(beneficiary?.participation),
      0
    );

    if (beneficiaries.length === 1) {
      const participation = Utils.Unmask.Percentage(beneficiaries[0]?.participation);
      if (participation !== 100) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          message: VALIDATION_MESSAGES.participation.total,
          path: ['0', 'participation'],
        });
      }
      return;
    }

    if (totalParticipation === 100) {
      return;
    }

    const errorMessage =
      totalParticipation > 100
        ? VALIDATION_MESSAGES.participation.maxValue
        : VALIDATION_MESSAGES.participation.total;

    beneficiaries.forEach((_, index) => {
      ctx.addIssue({
        code: zod.ZodIssueCode.custom,
        message: errorMessage,
        path: [index, 'participation'],
      });
    });
  }),
});

export const FORM_CONFIG = {
  mode: 'onChange' as const,
  reValidateMode: 'onChange' as const,
  criteriaMode: 'all' as const,
  defaultValues: {
    beneficiaries: [DEFAULT_BENEFICIARY],
  },
};
